import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { AccessMatrixService } from '../../services/access-matrix.service';
import { AccessGroupService } from '../../services/access-group.service';
import {
  AccessExportRequest,
  AccessGroup,
  AccessGroupMember,
  AccessGroupMembers,
  AccessMultiGroupMember,
  AccessOrphanEmployee
} from '../../models/access-group.model';
import {
  ACCESS_ROLES,
  AccessMatrixRow,
  AccessRole,
  RoleMeta
} from '../../models/access-matrix.model';

type AccessTab = 'matrix' | 'groups' | 'multi' | 'ungrouped';

/** Which stats card is acting as a filter; null means "all pages". */
type StatFilter = 'adminOnly' | 'broadlyOpen' | 'shared' | null;

/**
 * Admin view of which role reaches which page. The grid is derived from the route table at
 * runtime (see AccessMatrixService), so it always reports the access the guard actually
 * enforces rather than a separately maintained document.
 */
@Component({
  selector: 'app-access-matrix',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Select, Tooltip],
  templateUrl: './access-matrix.component.html',
  styleUrl: './access-matrix.component.scss'
})
export class AccessMatrixComponent implements OnInit, OnDestroy {
  private accessMatrixService = inject(AccessMatrixService);
  private accessGroupService = inject(AccessGroupService);
  private destroyRef = inject(DestroyRef);
  public pageHeaderService = inject(PageHeaderSearchActionService);

  readonly roles: RoleMeta[] = ACCESS_ROLES;

  activeTab: AccessTab = 'matrix';

  // --- By Group tab -------------------------------------------------------
  groups: AccessGroup[] = [];
  groupsLoading = false;
  groupsLoaded = false;
  groupsError = '';
  /** Second line of the error panel: what it means and what to do. */
  groupsErrorDetail = '';

  selectedGroupRole: AccessRole | null = null;
  groupMembers: AccessGroupMembers | null = null;
  membersLoading = false;

  ungrouped: AccessOrphanEmployee[] = [];
  ungroupedLoading = false;
  ungroupedLoaded = false;
  ungroupedError = '';

  // --- Multiple Groups tab ------------------------------------------------
  multiGroup: AccessMultiGroupMember[] = [];
  multiLoading = false;
  multiLoaded = false;
  multiError = '';

  /** Minimum role count to show; lets an admin jump straight to the widest access. */
  minRoleCount: number | null = null;
  minRoleCountOptions: { label: string; value: number | null }[] = [];
  selectedMultiRole: string | null = null;
  multiRoleOptions: { label: string; value: string | null }[] = [];

  /** Filters for the No Access Group tab. */
  selectedUngroupedRole: string | null = null;
  selectedUngroupedServiceGroup: string | null = null;
  ungroupedRoleOptions: { label: string; value: string | null }[] = [];
  ungroupedServiceGroupOptions: { label: string; value: string | null }[] = [];

  /** Hide members who match an active employee, leaving only the drift. */
  showOnlyDrift = false;

  /** Guards against a second Export while one is in flight. */
  exporting = false;

  allRows: AccessMatrixRow[] = [];
  rows: AccessMatrixRow[] = [];

  searchText = '';
  selectedRole: AccessRole | null = null;
  selectedArea: string | null = null;

  roleOptions: { label: string; value: AccessRole | null }[] = [];
  areaOptions: { label: string; value: string | null }[] = [];

  ngOnInit(): void {
    this.allRows = this.accessMatrixService.buildMatrix();

    this.roleOptions = [
      { label: 'All roles', value: null },
      ...this.roles.map(r => ({ label: r.label, value: r.role }))
    ];

    const areas = [...new Set(this.allRows.map(r => r.area))].sort();
    this.areaOptions = [
      { label: 'All areas', value: null },
      ...areas.map(a => ({ label: a, value: a }))
    ];

    // Search and the filter-bar toggle live in the app header, like every other list page,
    // rather than this page owning a second search box of its own.
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search pages...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);

    this.pageHeaderService.searchText
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(text => {
        this.searchText = text ?? '';
        this.applyFilters();
      });

    this.pageHeaderService.exportClick
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.exportExcel());

    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  /**
   * Page-name sort direction: null = the natural area/title order, true = A→Z,
   * false = Z→A. Applied within each area (see sortMatrix).
   */
  matrixSortAsc: boolean | null = null;

  /** Cycles the Page column A→Z, Z→A, then back to the natural order. */
  sortMatrix(): void {
    this.matrixSortAsc =
      this.matrixSortAsc === null ? true : this.matrixSortAsc ? false : null;
    this.applyFilters();
  }

  /**
   * A page counts as "broadly open" once roughly two thirds of the roles can reach it.
   * Derived from the role count rather than fixed, so adding a role (Operations and
   * Support arrived after this screen was built) does not quietly change what the card
   * means — at ten roles this is 7, at twelve it is 8.
   */
  readonly broadlyOpenThreshold = Math.ceil(ACCESS_ROLES.length * 2 / 3);

  /** Active stats-card filter. */
  statFilter: StatFilter = null;

  /** Clicking the active card clears it, so a card is a toggle rather than a dead end. */
  setStatFilter(filter: StatFilter): void {
    this.statFilter = this.statFilter === filter ? null : filter;
    this.applyFilters();
  }

  /**
   * Single definition of what each card counts, used for both the card's number and its
   * filter — so the table can never disagree with the figure that was clicked.
   */
  private matchesStat(row: AccessMatrixRow, filter: StatFilter): boolean {
    switch (filter) {
      case 'adminOnly':
        return row.accessCount === 1 && row.cells[0].kind === 'direct';
      case 'broadlyOpen':
        return row.accessCount >= this.broadlyOpenThreshold;
      case 'shared':
        // Everything between the two extremes, so the three cards partition the pages.
        return row.accessCount > 1 && row.accessCount < this.broadlyOpenThreshold;
      default:
        return true;
    }
  }

  applyFilters(): void {
    const term = this.searchText.trim().toLowerCase();

    this.rows = this.allRows.filter(row => {
      if (!this.matchesStat(row, this.statFilter)) return false;

      if (this.selectedArea && row.area !== this.selectedArea) return false;

      // Filtering by role answers "what can this role reach?" — so only pages the role
      // actually reaches survive, inherited access included.
      if (this.selectedRole) {
        const cell = row.cells.find(c => c.role === this.selectedRole);
        if (!cell || cell.kind === 'none') return false;
      }

      if (term) {
        const haystack = `${row.title} ${row.path} ${row.area}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }

      return true;
    });

    // Area always leads, so the grouped subheaders stay intact; the Page sort only
    // reorders rows inside their own area.
    const direction = this.matrixSortAsc === false ? -1 : 1;
    this.rows.sort(
      (a, b) =>
        a.area.localeCompare(b.area) || direction * a.title.localeCompare(b.title)
    );
  }

  clearFilters(): void {
    this.selectedRole = null;
    this.selectedArea = null;
    this.statFilter = null;
    // Clearing the header search pushes back through the subscription above, which
    // re-runs applyFilters — so this method must not also set searchText directly.
    this.pageHeaderService.setSearchText('');
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText || this.selectedRole || this.selectedArea || this.statFilter);
  }

  /** Pages reachable by a role across the whole app — the column totals. */
  pageCountFor(role: AccessRole): number {
    return this.allRows.filter(
      row => row.cells.find(c => c.role === role)?.kind !== 'none'
    ).length;
  }

  /** Pages only an admin can open; the "tightest" pages in the system. */
  get adminOnlyCount(): number {
    return this.allRows.filter(r => this.matchesStat(r, 'adminOnly')).length;
  }

  /** Pages open to several roles but not most of them. */
  get sharedCount(): number {
    return this.allRows.filter(r => this.matchesStat(r, 'shared')).length;
  }

  /** Pages reachable by at least `broadlyOpenThreshold` of the roles. */
  get broadlyOpenCount(): number {
    return this.allRows.filter(r => this.matchesStat(r, 'broadlyOpen')).length;
  }

  tooltipFor(row: AccessMatrixRow, role: AccessRole): string {
    const cell = row.cells.find(c => c.role === role);
    const label = this.roles.find(r => r.role === role)?.label ?? role;
    if (!cell || cell.kind === 'none') return `${label} cannot open ${row.title}`;
    if (cell.kind === 'inherited') {
      return `${label} reaches ${row.title} through an implied role, not by being listed`;
    }
    return `${label} is listed directly on ${row.title}`;
  }


  // --- By Group tab -------------------------------------------------------

  selectTab(tab: AccessTab): void {
    this.activeTab = tab;

    // The matrix and the no-access list are both filterable, so each gets the header's
    // search box and filter icon with its own placeholder. Group Members is driven by the
    // group chips instead, so it offers neither. Export works on every tab.
    const searchable = tab === 'matrix' || tab === 'ungrouped' || tab === 'multi';
    this.pageHeaderService.setCanShowFilterToggle(searchable);
    this.pageHeaderService.setPlaceholder(
      tab === 'matrix' ? 'Search pages...' :
      tab === 'ungrouped' ? 'Search employees...' :
      tab === 'multi' ? 'Search people...' : ''
    );
    this.pageHeaderService.setCanShowExport(true);

    // Each tab filters its own data, so a term typed on one must not silently narrow
    // the other.
    this.pageHeaderService.setSearchText('');

    // Entra is only queried when a tab that needs it is opened, so the matrix stays instant.
    if (tab === 'groups' && !this.groupsLoaded) {
      this.loadGroups(false);
    }
    if (tab === 'multi' && !this.multiLoaded) {
      this.loadMultiGroup();
    }
    if (tab === 'ungrouped' && !this.ungroupedLoaded) {
      this.loadUngrouped(false);
    }
  }

  loadGroups(refresh: boolean): void {
    this.groupsLoading = true;
    this.groupsError = '';
    this.groupsErrorDetail = '';

    this.accessGroupService.getGroups(refresh)
      .pipe(catchError(err => {
        this.setGroupsError(err);
        return of<AccessGroup[]>([]);
      }))
      .subscribe(groups => {
        this.groups = groups;
        this.groupsLoading = false;
        this.groupsLoaded = true;

        // A successful call that returns nothing readable is still a dead end for the
        // user, so it gets an explanation rather than a blank tab.
        if (!this.groupsError) {
          if (groups.length === 0) {
            this.groupsError = 'No access groups are configured';
            this.groupsErrorDetail =
              'The Entra group IDs are missing from the server configuration.';
          } else if (groups.every(g => !g.isConfigured || g.memberCount === null)) {
            this.groupsError = 'No groups could be read from Entra';
            this.groupsErrorDetail =
              'The groups are configured, but Microsoft Entra did not return their members. '
              + 'This usually means the directory permission was revoked.';
          }
        }

        if (this.groupsError) return;

        // Open the first readable group so the tab is never an empty shell.
        if (!this.selectedGroupRole) {
          const first = groups.find(g => g.isConfigured && g.memberCount !== null);
          if (first) this.selectGroup(first.role, refresh);
        } else {
          this.loadMembers(this.selectedGroupRole, refresh);
        }
      });
  }

  /** Turns an HTTP failure into something a person can act on. */
  private setGroupsError(err: unknown): void {
    const status = (err as { status?: number })?.status;

    if (status === 403) {
      this.groupsError = 'You do not have permission to view access groups';
      this.groupsErrorDetail = 'This page is restricted to the Admin group.';
    } else if (status === 0) {
      this.groupsError = 'Could not reach the server';
      this.groupsErrorDetail = 'Check your connection and try again.';
    } else {
      this.groupsError = 'Could not load access groups';
      this.groupsErrorDetail = 'The server did not respond as expected. Try again shortly.';
    }
  }

  /** Re-runs whichever load the current tab needs, after a failure. */
  retry(): void {
    if (this.activeTab === 'groups') {
      this.groupsLoaded = false;
      this.loadGroups(true);
    } else if (this.activeTab === 'multi') {
      this.multiLoaded = false;
      this.loadMultiGroup();
    } else if (this.activeTab === 'ungrouped') {
      this.loadUngrouped(true);
    }
  }

  selectGroup(role: AccessRole, refresh = false): void {
    this.selectedGroupRole = role;
    this.loadMembers(role, refresh);
  }

  private loadMembers(role: AccessRole, refresh: boolean): void {
    this.membersLoading = true;
    this.groupMembers = null;

    this.accessGroupService.getMembers(role, refresh)
      .pipe(catchError(() => of<AccessGroupMembers>({
        role, label: role, groupId: '', members: [], withoutActiveEmployee: [],
        error: 'This group could not be read from Entra. Try selecting it again shortly.'
      })))
      .subscribe(result => {
        this.groupMembers = result;
        this.membersLoading = false;
      });
  }

  private loadMultiGroup(): void {
    this.multiLoading = true;
    this.multiError = '';

    this.accessGroupService.getMultiGroupMembers()
      .pipe(catchError(() => {
        this.multiError = 'Could not load group membership';
        return of<AccessMultiGroupMember[]>([]);
      }))
      .subscribe(list => {
        this.multiGroup = list;
        this.multiLoading = false;
        this.multiLoaded = true;

        // Options come from the data, so neither filter can select a value that
        // matches nothing.
        const counts = [...new Set(list.map(p => p.roleCount))].sort((a, b) => a - b);
        this.minRoleCountOptions = [
          { label: 'Any number of roles', value: null },
          ...counts.map(c => ({ label: `${c} or more roles`, value: c }))
        ];

        const roles = [...new Set(list.flatMap(p => p.roles))].sort();
        this.multiRoleOptions = [
          { label: 'All roles', value: null },
          ...roles.map(r => ({ label: r, value: r }))
        ];
      });
  }

  /** The Multiple Groups rows after its own search and filters. */
  get filteredMultiGroup(): AccessMultiGroupMember[] {
    const term = this.searchText.trim().toLowerCase();

    return this.multiGroup.filter(person => {
      if (this.minRoleCount !== null && person.roleCount < this.minRoleCount) return false;
      if (this.selectedMultiRole && !person.roles.includes(this.selectedMultiRole)) return false;
      if (term) {
        const haystack = [person.displayName, person.email, person.jobTitle, ...person.roles]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }

  hasActiveMultiFilters(): boolean {
    return !!(this.searchText || this.minRoleCount !== null || this.selectedMultiRole);
  }

  clearMultiFilters(): void {
    this.minRoleCount = null;
    this.selectedMultiRole = null;
    this.pageHeaderService.setSearchText('');
  }

  /** People holding most of the roles; the headline number on this tab. */
  get widestAccessCount(): number {
    return this.multiGroup.filter(p => p.roleCount >= this.broadlyOpenThreshold).length;
  }

  /** People flagged for a reason beyond role count. */
  get multiNeedsReviewCount(): number {
    return this.multiGroup.filter(p => p.needsReview).length;
  }

  private loadUngrouped(refresh: boolean): void {
    this.ungroupedLoading = true;
    this.ungroupedError = '';

    this.accessGroupService.getUngroupedEmployees(refresh)
      .pipe(catchError(() => {
        // Swallowing this would render the empty state, which claims everyone has
        // access — the opposite of what a failed check actually knows.
        this.ungroupedError = 'Could not check employee access';
        return of<AccessOrphanEmployee[]>([]);
      }))
      .subscribe(list => {
        this.ungrouped = list;
        this.ungroupedLoading = false;
        this.ungroupedLoaded = true;

        // Options come from the data itself, so the dropdowns never offer a value that
        // would filter to nothing.
        const roles = [...new Set(list.map(e => e.role).filter((r): r is string => !!r))].sort();
        this.ungroupedRoleOptions = [
          { label: 'All roles', value: null },
          ...roles.map(r => ({ label: r, value: r }))
        ];

        const serviceGroups = [
          ...new Set(list.map(e => e.serviceGroup).filter((g): g is string => !!g))
        ].sort();
        this.ungroupedServiceGroupOptions = [
          { label: 'All service groups', value: null },
          ...serviceGroups.map(g => ({ label: g, value: g }))
        ];
      });
  }

  /** The No Access Group rows after its own search and filters. */
  get filteredUngrouped(): AccessOrphanEmployee[] {
    const term = this.searchText.trim().toLowerCase();

    return this.ungrouped.filter(employee => {
      if (this.selectedUngroupedRole && employee.role !== this.selectedUngroupedRole) {
        return false;
      }
      if (this.selectedUngroupedServiceGroup
          && employee.serviceGroup !== this.selectedUngroupedServiceGroup) {
        return false;
      }
      if (term) {
        const haystack = [employee.name, employee.email, employee.role, employee.jobTitle]
          .filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }

  hasActiveUngroupedFilters(): boolean {
    return !!(this.searchText || this.selectedUngroupedRole || this.selectedUngroupedServiceGroup);
  }

  clearUngroupedFilters(): void {
    this.selectedUngroupedRole = null;
    this.selectedUngroupedServiceGroup = null;
    this.pageHeaderService.setSearchText('');
  }



  /** Members shown in the table, honouring the drift-only filter. */
  get visibleMembers(): AccessGroupMember[] {
    const members = this.groupMembers?.members ?? [];
    return this.showOnlyDrift
      ? members.filter(m => m.isActiveEmployee !== true)
      : members;
  }

  get driftCount(): number {
    return this.groupMembers?.withoutActiveEmployee.length ?? 0;
  }

  /** Why a member is flagged, or '' when they match an active employee cleanly. */
  driftReason(member: AccessGroupMember): string {
    if (!member.hasEmployeeRecord) return 'No employee record';
    if (member.isActiveEmployee === false) return 'Employee is inactive';
    // Someone who cannot sign in should not still hold a role, even when their employee
    // record is active — this is the same stale access the other two cases describe.
    if (member.accountEnabled === false) return 'Sign-in disabled';
    return '';
  }

  selectedGroup(): AccessGroup | undefined {
    return this.groups.find(g => g.role === this.selectedGroupRole);
  }

  /**
   * Downloads a styled .xlsx of the tab currently on screen. The workbook is built
   * server-side with ClosedXML — a browser-side build silently drops header fills and
   * frozen rows — so the request carries the rows and the server renders them.
   */
  exportExcel(): void {
    if (this.exporting) return;

    const request: AccessExportRequest = {
      roleLabels: this.roles.map(r => r.label),
      matrix: [],
      groups: [],
      multiGroup: [],
      ungrouped: [],
      scope: 'Access_Matrix'
    };

    if (this.activeTab === 'matrix') {
      // The filtered rows, so the file matches what is on screen.
      request.matrix = this.rows.map(row => ({
        area: row.area,
        title: row.title,
        path: row.path,
        access: row.cells.map(c =>
          c.kind === 'direct' ? 'Yes' : c.kind === 'inherited' ? 'Inherited' : '')
      }));
      request.scope = 'Access_Matrix';
    } else if (this.activeTab === 'groups') {
      // The server resolves every group's membership itself; this screen only ever holds
      // the group the user has open, so sending its own list would export one group and
      // leave the other nine empty.
      request.includeGroups = true;
      request.scope = 'Access_Groups';
    } else if (this.activeTab === 'multi') {
      // Resolved server-side like the groups tab: the pivot needs every group's
      // membership, which this screen does not hold.
      request.includeMultiGroup = true;
      request.scope = 'Multiple_Groups';
    } else {
      // The filtered rows, so the file matches what is on screen.
      request.ungrouped = this.filteredUngrouped;
      request.scope = 'No_Access_Group';
    }

    this.exporting = true;
    this.pageHeaderService.setExportDisabled(true);

    this.accessGroupService.exportExcel(request)
      .pipe(catchError(() => of<Blob | null>(null)))
      .subscribe(blob => {
        this.exporting = false;
        this.pageHeaderService.setExportDisabled(false);
        if (blob) this.saveBlob(blob, request.scope!);
      });
  }


  private saveBlob(blob: Blob, scope: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${scope}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
